/**
 * Frontegg shim.
 *
 * When VUE_APP_USE_COGNITO_AUTH=true, Frontegg is NOT initialised and calling
 * its composables directly crashes with "Cannot read properties of undefined
 * (reading 'getCurrentRoute')".
 *
 * Every file that imports `useFrontegg` / `useFronteggAuthGuard` should import
 * from THIS shim instead of "@frontegg/vue":
 *
 *   import { useFrontegg, useFronteggAuthGuard } from "@/cognito/frontegg-shim";
 *
 * When Cognito is active the shim returns safe no-op values that keep all
 * existing component logic working (guards are skipped, authState.user is null,
 * fronteggLoaded is true so components don't hang on a loading screen).
 *
 * When Frontegg is active the shim delegates to the real @frontegg/vue package,
 * so behaviour is 100% identical to before.  Remove this file once migration
 * is complete and replace the import paths back to "@frontegg/vue".
 */

import { useCognitoAuth } from "./cognito.config";
import { cognitoSignIn } from "./auth";
import { peekCrossAppHandoffEmail } from "./crossAppLogoutCookie";
import { ref } from "vue";

// ---------------------------------------------------------------------------
// Safe mock shapes (used when Cognito is active)
// ---------------------------------------------------------------------------

const mockAuthState = {
  user: null as null | Record<string, unknown>,
  isAuthenticated: false,
  isLoading: false,
};

function mockUseFrontegg() {
  return {
    fronteggLoaded: ref(true),
    authState: mockAuthState,
    /** Same UX as Frontegg: send user through Cognito Hosted UI (PKCE). */
    loginWithRedirect: () => {
      if (peekCrossAppHandoffEmail()) {
        return;
      }
      void cognitoSignIn();
    },
  };
}

function mockUseFronteggAuthGuard() {
  // No-op — Cognito handles its own routing guard
}

// ---------------------------------------------------------------------------
// Re-exports
// ---------------------------------------------------------------------------

export function useFrontegg() {
  if (useCognitoAuth) {
    return mockUseFrontegg();
  }
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  return require("@frontegg/vue").useFrontegg();
}

export function useFronteggAuthGuard() {
  if (useCognitoAuth) {
    return mockUseFronteggAuthGuard();
  }
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  return require("@frontegg/vue").useFronteggAuthGuard();
}
